import { useState, type JSX } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2, UserPlus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Note, Segmented } from '../workspace/ui';
import { Button } from '@/components/ui/button';
import { useDiscoveryClient } from '../api/context';
import type { AddUsernamesResult, Folder } from '../api/types';
import { useCapabilities } from '../hooks/capabilities';
import { PLATFORM_LABELS, type Platform } from '../lib/platform';

/** Pasted links say which platform they're from; use that when they all agree. */
function detectPlatform(text: string): Platform | null {
  const ig = /instagram\.com\//i.test(text);
  const x = /(?:^|[/.])(?:x|twitter)\.com\//i.test(text);
  const tt = /tiktok\.com\//i.test(text);
  if (ig && !x && !tt) return 'instagram';
  if (x && !ig && !tt) return 'x';
  if (tt && !ig && !x) return 'tiktok';
  return null;
}

/**
 * Add creators to a folder by pasting handles.
 *
 * Runs as a normal search job rather than inserting rows, because pasted
 * handles need exactly what searched ones get: enrichment, registry dedupe,
 * outreach vetting and provenance. Inserting directly would produce leads with
 * no follower counts — the data every later step reads.
 *
 * That also means the folder's live progress panel picks it up with no extra
 * wiring, so a paste of forty handles is watchable rather than a spinner.
 *
 * Parsing is left to the server (`parseHandles`): it already accepts `@name`,
 * bare handles and full profile URLs across newlines, commas and spaces, and
 * duplicating that here would give two subtly different answers about what
 * counts as valid.
 */
export function AddCreatorsDialog({
  folder,
  onClose,
  onStarted,
}: {
  folder: Folder;
  onClose: () => void;
  onStarted: (result: AddUsernamesResult) => void;
}): JSX.Element {
  const client = useDiscoveryClient();
  const [text, setText] = useState('');
  const [chosen, setChosen] = useState<Platform>('tiktok');
  const platforms: Platform[] = useCapabilities().platforms ?? ['tiktok'];
  const detected = detectPlatform(text);
  const platform = detected ?? chosen;

  const addMutation = useMutation({
    mutationFn: () => client.folders.addUsernames(folder.publicId, text, platform),
    onSuccess: onStarted,
  });

  // A rough count for the button label only. The server decides what's valid;
  // this is just so the button doesn't say "Add" with nothing to add.
  const roughCount = text.split(/[\n,]+/).map((t) => t.trim()).filter(Boolean).length;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-4 w-4 text-brand" strokeWidth={2} />
            Add creators to “{folder.name}”
          </DialogTitle>
          <DialogDescription>
            Paste handles, profile links, or both — one per line or comma separated. Each one is looked up and added
            with full stats.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          {platforms.length > 1 && (
            <div className="grid gap-1">
              <span className="text-sm font-medium">Platform</span>
              <div className="flex flex-wrap items-center gap-2">
                <Segmented
                  label="Platform"
                  value={platform}
                  onChange={(p) => !detected && setChosen(p)}
                  options={platforms.map((p) => ({ value: p, label: PLATFORM_LABELS[p] }))}
                  className={cn('w-fit', !!detected && 'pointer-events-none opacity-70')}
                />
                {detected && <span className="text-sm text-muted-foreground">Detected from the links</span>}
              </div>
            </div>
          )}
          <textarea
            aria-label="Handles or links"
            className="min-h-[9rem] w-full resize-y rounded-lg border border-input bg-[var(--input-bg)] px-3 py-2 font-mono text-sm shadow-control placeholder:text-muted-foreground/70 focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/25"
            placeholder={'@viclasala\nhttps://www.tiktok.com/@prettygirlamy_\njuliahlav'}
            value={text}
            onChange={(ev) => setText(ev.target.value)}
            autoFocus
          />
          <p className="text-sm text-muted-foreground">
            Creators already in another folder are still added here — naming someone explicitly is a request, not a
            search.
          </p>
          {addMutation.error && <Note tone="error">{(addMutation.error as Error).message}</Note>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={roughCount === 0 || addMutation.isPending} onClick={() => addMutation.mutate()}>
            {addMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {addMutation.isPending ? 'Starting…' : `Add ${roughCount || ''}`.trim()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
